import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { ROUTES } from '../../../constants/RouteNameMapping';
import {
  DocumentsDetectedEvent,
  IDocumentsDetectedMessageDataModel,
} from '../../../data-models/documents-detected.data-model';
import { IGenericSocketUpdate } from '../../../data-models/socket-base.data-model';
import { useSocketEventSubscription } from '../../../services/hooks/useSocketEventSubscription';
import { clientConfigFieldsAtom } from '../../../services/state/AppConfigStateJ';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { DOCUMENTS_QUERY_KEY } from './useDocuments';

/**
 * Consumes the detection-complete socket event and shows an in-app toast linking
 * to the Document Hub, where the review flow auto-opens for detected documents.
 * Best-effort — the event is lost if the user is offline. Gated on the same
 * conditions as the `/docs` route so users without it never get a dead-end toast.
 */
export function useDocumentsDetectedSubscription(): void {
  const { showDocumentHub } = useFlags<MaggieFeatureFlags>();
  const usingConsolidatedDataCollection = Boolean(
    useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection
  );
  const { pushInfoToast } = useToastMessageState();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const handleDocumentsDetected = useCallback(
    (update: IGenericSocketUpdate<IDocumentsDetectedMessageDataModel>) => {
      if (!update.data?.documentIds?.length) {
        return;
      }
      // Refresh an already-open list immediately instead of waiting for the poll.
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] });

      // No toast when the user is already on the Document Hub — the list refresh
      // above surfaces the detected docs, so a notification would be redundant.
      const docsPath = `/${ROUTES.DOCS}`;
      if (location.pathname === docsPath || location.pathname.startsWith(`${docsPath}/`)) {
        return;
      }

      pushInfoToast({
        title: 'Documents ready for review',
        message: 'Detection finished. Review them in Documents.',
        buttonLabel: 'Review',
        buttonAction: () => {
          navigate(`/${ROUTES.DOCS}`);
        },
      });
    },
    [location.pathname, navigate, pushInfoToast, queryClient]
  );

  useSocketEventSubscription(DocumentsDetectedEvent, handleDocumentsDetected, {
    enabled: showDocumentHub && usingConsolidatedDataCollection,
  });
}
